/* eslint-disable import/no-cycle */

import React from 'react';
import PropTypes from 'prop-types';

import { Heading } from '@components';
import { useContext } from '@hooks';

import { LinksContext } from './Links';

/**
 * Links.Heading component that displays a heading within a `Links` component.
 * Must be used as a child of a `Links` component.
 *
 * @component Links.Heading
 * @param {Object} props - The properties for the Links.Heading component.
 * @param {React.ReactNode} props.children - The content of the heading.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the heading.
 * @param {string} [props.as] - Optional HTML element to render as the heading. Defaults to 'h2'.
 * @param {Object} [restProps] - Additional properties to be passed to the heading element.
 * @returns {ReactElement|null} The rendered Links.Heading component, or `NULL` if no children are provided.
 * @example
 * <Links>
 *   <Links.Heading>Section Title</Links.Heading>
 *   <Links.Link href="/example">Example 1</Links.Link>
 * </Links>
 */
export default function LinksHeading({
  children,
  className,
  as,
  ...restProps
}) {
  useContext(LinksContext, 'Links.Heading must be a child of Links.');

  if (!children) return null;

  return (
    <Heading
      as={as}
      className="mb-3 text-base tracking-wider text-[#6B6B6B] md:text-base"
      {...restProps}
    >
      {children}
    </Heading>
  );
}

LinksHeading.propTypes = {
  children: PropTypes.string.isRequired,
  className: PropTypes.string,
  as: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4'])
};

LinksHeading.defaultProps = {
  className: '',
  as: 'h2'
};
